import { VocalLib, midiNoteNumberFromFrequency, midiToNoteLabel, type PitchFrame } from '@tunasong/music-lib'
import type { FC } from 'react'
import styles from './pitch-input-meter.module.css'

/**
 * Live input meter, shown while the window is open.
 *
 * The tool's first user report was "there is no indication that the mic is recording at a proper
 * level" — and the diagnosis only arrived *after* a take had already failed. A singer needs to
 * know before they sing that the microphone is alive, that it is the right one, and that their
 * level clears the gate their take will be judged against. All three are in the frames the
 * capture is already collecting, so this opens no second stream and enables no second feature.
 *
 * @see docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.1, §9
 */

/** Above this the converter is close enough to full scale that the tracker starts reading clipping. */
const TOO_LOUD_DB = -3

/**
 * Below the voicing gate but still periodic enough to be worth showing, marked as doubtful.
 *
 * The interesting band: a singer sitting here is producing a pitch the tracker can see and the
 * scorer will throw away. Showing it bare would promise a score that is not coming; showing
 * nothing would hide the single most useful clue about whether the 0.90 gate is too strict for
 * this voice on this hardware.
 */
const DOUBTFUL_CLARITY = 0.6

const toDb = (rms: number) => 20 * Math.log10(Math.max(rms, 1e-5))

type LevelZone = 'too quiet' | 'good' | 'too loud'

const zoneOf = (levelDb: number, gateDb: number): LevelZone => {
  if (levelDb >= TOO_LOUD_DB) {
    return 'too loud'
  }
  return levelDb >= gateDb ? 'good' : 'too quiet'
}

const pitchOf = (frame: PitchFrame | null): string => {
  if (!frame || frame.frequency <= 0 || frame.clarity < DOUBTFUL_CLARITY) {
    return '—'
  }
  const name = midiToNoteLabel(midiNoteNumberFromFrequency(frame.frequency))
  return frame.clarity >= VocalLib.voicingClarityMin ? name : `~${name}`
}

/** Everything the meter needs, as one prop — the host owns all three and passes them together. */
export interface PitchMeterState {
  frame: PitchFrame | null
  noiseFloorRms: number
  deviceLabel: string | null
}

export interface PitchInputMeterProps {
  /** The most recent analysis frame, or null before any has arrived. */
  frame: PitchFrame | null
  /** The measured noise floor, linear RMS. The gate sits `voicingFloorDb` above it. */
  noiseFloorRms: number
  /** The microphone the capture opened. Empty or null when the browser withheld it. */
  deviceLabel: string | null
}

export const PitchInputMeter: FC<PitchInputMeterProps> = props => {
  const { frame, noiseFloorRms, deviceLabel } = props

  const levelDb = toDb(frame?.rms ?? 0)
  const gateDb = toDb(noiseFloorRms) + VocalLib.voicingFloorDb
  const zone = zoneOf(levelDb, gateDb)

  /** -60 dBFS to 0 across the bar — below -60 there is nothing to look at. */
  const fill = Math.max(0, Math.min(100, ((levelDb + 60) / 60) * 100))

  return (
    <div className={styles.container}>
      <div className={styles.row} aria-label="Input level">
        <div
          className={styles.meter}
          role="meter"
          aria-label="Input level in decibels"
          aria-valuenow={Math.round(levelDb)}
          aria-valuemin={-60}
          aria-valuemax={0}
        >
          <div className={styles.fill} data-zone={zone} style={{ width: `${fill}%` }} />
          {/** Where the voicing gate sits, so "too quiet" is a place and not just a word. */}
          <div className={styles.gate} style={{ left: `${Math.max(0, Math.min(100, ((gateDb + 60) / 60) * 100))}%` }} />
        </div>
        <span className={styles.zone} data-zone={zone}>
          {zone}
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.pitch} aria-label="Detected pitch" data-doubtful={pitchOf(frame).startsWith('~')}>
          {pitchOf(frame)}
        </span>
        {deviceLabel ? <span className={styles.device}>{deviceLabel}</span> : null}
      </div>
    </div>
  )
}
